// InsuranceDetailScreen.js
// Shows Insurance Policy section matching the desktop layout.
// Editable for Held inspections, read-only for completed.
// Shows existing policies grid with renew options.

/* eslint-disable react-hooks/exhaustive-deps */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
};


const addMonths = (date, months) => {
  const d = new Date(date);
  d.setMonth(d.getMonth() + months);
  return d;
};

export default function InsuranceDetailScreen() {
  const { getClient } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { pkInspection, fkCustomer, fkInsurance, isReadOnly, historyState } = location.state || {};

  const [insurances, setInsurances] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [showNewForm, setShowNewForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    naic: '',
    insuranceCompanyName: '',
    policyNumber: '',
    expirationDate: '',
    selfInsured: false,
    noExpiration: false,
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const client = getClient();
      const [insRes, compRes] = await Promise.all([
        client.get(`/api/insurance/customer/${fkCustomer}`),
        client.get('/api/insurance/companies'),
      ]);
      setInsurances(insRes.data || []);
      setCompanies(compRes.data || []);
    } catch {
      setError('Could not load insurance.');
    } finally {
      setLoading(false);
    }
  }, [fkCustomer, getClient]);

  useEffect(() => { load(); }, []);

  const update = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const openNewForm = () => {
    setForm({ naic: '', insuranceCompanyName: '', policyNumber: '', expirationDate: '', selfInsured: false, noExpiration: false });
    setEditingId(null);
    setShowNewForm(true);
  };

  const openEditForm = (ins) => {
    setForm({
      naic: ins.naic || '',
      insuranceCompanyName: ins.insuranceCompanyName || '',
      policyNumber: ins.policyNumber || '',
      expirationDate: ins.expirationDate ? ins.expirationDate.substring(0, 10) : '',
      selfInsured: ins.selfInsured || false,
      noExpiration: ins.noExpiration || false,
    });
    setEditingId(ins.pkInsurance);
    setShowNewForm(true);
  };

  const openRenewForm = (ins, months) => {
    const newExp = ins.expirationDate
      ? addMonths(new Date(ins.expirationDate), months)
      : addMonths(new Date(), months);
    setForm({
      naic: ins.naic || '',
      insuranceCompanyName: ins.insuranceCompanyName || '',
      policyNumber: ins.policyNumber || '',
      expirationDate: newExp.toISOString().substring(0, 10),
      selfInsured: ins.selfInsured || false,
      noExpiration: ins.noExpiration || false,
    });
    setEditingId(null); // new record
    setShowNewForm(true);
  };

  const handleSaveInsurance = async () => {
    setSaving(true);
    setError('');
    try {
      const client = getClient();
      const payload = {
        fkCustomer,
        naic: form.naic,
        insuranceCompanyName: form.insuranceCompanyName,
        policyNumber: form.policyNumber,
        expirationDate: form.noExpiration ? null : (form.expirationDate ? new Date(form.expirationDate).toISOString() : null),
        selfInsured: form.selfInsured,
        noExpiration: form.noExpiration,
      };
      if (editingId) {
        await client.put(`/api/insurance/${editingId}`, payload);
      } else {
        await client.post('/api/insurance', payload);
      }
      setShowNewForm(false);
      await load();
    } catch {
      setError('Could not save insurance.');
    } finally {
      setSaving(false);
    }
  };

  const handleNaicLookup = async () => {
    if (!form.naic) return;
    try {
      const client = getClient();
      const res = await client.get(`/api/insurance/companies/naic/${form.naic}`);
      if (res.data?.insuranceCompanyName) {
        update('insuranceCompanyName', res.data.insuranceCompanyName);
      }
    } catch {
      // Not found — user enters manually
    }
  };

  if (loading) return <div style={s.page}><div style={s.center}><div style={s.spinner} /></div></div>;

  return (
    <div style={s.page}>
      <div style={s.header}>
        <span style={s.headerTitle}>Insurance</span>
        {pkInspection && <span style={s.headerSub}>Inspection #{pkInspection}</span>}
      </div>

      {error && <div style={s.error}>{error}</div>}

      <div style={s.body}>
        {/* New/Edit Insurance Form */}
        {showNewForm && !isReadOnly && (
          <div style={s.section}>
            <div style={s.sectionTitle}>{editingId ? 'Edit Insurance Policy' : 'New Insurance Policy'}</div>

            <div style={s.checkRow}>
              <label style={s.checkLabel}>
                <input type="checkbox" checked={form.selfInsured} onChange={e => update('selfInsured', e.target.checked)} />
                Self-Insured
              </label>
              <label style={s.checkLabel}>
                <input type="checkbox" checked={form.noExpiration} onChange={e => update('noExpiration', e.target.checked)} />
                No Expiration
              </label>
            </div>

            <div style={s.fieldRow}>
              <div style={s.field}>
                <label style={s.label}>NAIC Code</label>
                <div style={{ display: 'flex', gap: 6 }}>
                  <input style={s.input} value={form.naic} onChange={e => update('naic', e.target.value)} />
                  <button style={s.naicBtn} onClick={handleNaicLookup} type="button">Look Up</button>
                </div>
              </div>
            </div>

            <div style={s.field}>
              <label style={s.label}>Company Name</label>
              <div style={{ display: 'flex', gap: 6 }}>
                <input style={{ ...s.input, flex: 1 }} value={form.insuranceCompanyName} onChange={e => update('insuranceCompanyName', e.target.value)} list="companies-list" />
                <datalist id="companies-list">
                  {companies.map(c => <option key={c.pkInsuranceCompany} value={c.insuranceCompanyName} />)}
                </datalist>
              </div>
            </div>

            <div style={s.fieldRow}>
              <div style={s.field}>
                <label style={s.label}>Policy Number</label>
                <input style={s.input} value={form.policyNumber} onChange={e => update('policyNumber', e.target.value)} />
              </div>
              <div style={s.field}>
                <label style={s.label}>Expiration Date</label>
                <input style={s.input} type="date" value={form.expirationDate} onChange={e => update('expirationDate', e.target.value)} disabled={form.noExpiration} />
              </div>
            </div>

            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <button style={s.applyBtn} onClick={handleSaveInsurance} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
              <button style={s.closeBtn} onClick={() => setShowNewForm(false)}>Cancel</button>
            </div>
          </div>
        )}

        {/* Existing Insurance Policies */}
        <div style={s.section}>
          <div style={s.sectionTitle}>Existing Insurance Policies</div>
          <table style={s.table}>
            <thead>
              <tr style={s.thead}>
                <th style={s.th}>Company Name</th>
                <th style={s.th}>NAIC</th>
                <th style={s.th}>Policy Number</th>
                <th style={s.th}>Expiration</th>
                {!isReadOnly && <th style={s.th}>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {insurances.length === 0 ? (
                <tr><td colSpan={5} style={s.empty}>No insurance records found.</td></tr>
              ) : insurances.map((ins, idx) => {
                const isSelected = ins.pkInsurance === fkInsurance;
                const isExpired = ins.expirationDate && new Date(ins.expirationDate) < new Date();
                return (
                  <tr key={ins.pkInsurance} style={{
                    backgroundColor: isSelected ? '#e8f0ff' : idx % 2 === 0 ? '#fff' : '#f9f9f9',
                  }}>
                    <td style={s.td}>{ins.insuranceCompanyName} {isSelected && <span style={s.selectedBadge}>Current</span>}</td>
                    <td style={s.td}>{ins.naic}</td>
                    <td style={s.td}>{ins.policyNumber}</td>
                    <td style={{ ...s.td, color: isExpired ? '#cc0000' : 'inherit' }}>
                      {ins.noExpiration ? 'No Expiration' : formatDate(ins.expirationDate)}
                      {isExpired && ' — EXPIRED'}
                    </td>
                    {!isReadOnly && (
                      <td style={s.td}>
                        <div style={{ display: 'flex', gap: 4 }}>
                          <button style={s.actionBtn} onClick={() => openEditForm(ins)}>Edit</button>
                          <button style={s.actionBtn} onClick={() => openRenewForm(ins, 6)}>+6 Mo</button>
                          <button style={s.actionBtn} onClick={() => openRenewForm(ins, 12)}>+1 Yr</button>
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Bottom buttons matching desktop */}
        {!isReadOnly && (
          <div style={s.btnRow}>
            <button style={s.newPolicyBtn} onClick={openNewForm}>New Policy</button>
            <button style={s.renewBtn} onClick={() => {
              const current = insurances.find(i => i.pkInsurance === fkInsurance);
              if (current) openRenewForm(current, 6);
            }}>Renew 6 Months</button>
            <button style={s.renewBtn} onClick={() => {
              const current = insurances.find(i => i.pkInsurance === fkInsurance);
              if (current) openRenewForm(current, 12);
            }}>Renew 1 Year</button>
          </div>
        )}
      </div>

      {/* Footer */}
      <div style={s.footer}>
        <button style={s.closeBtn} onClick={() => navigate(`/inspection/${pkInspection}`, { state: { pkInspection, historyState } })}>
          Close
        </button>
      </div>
    </div>
  );
}

const s = {
  page:         { minHeight: '100vh', display: 'flex', flexDirection: 'column', backgroundColor: '#f0f0f0', fontFamily: 'system-ui, sans-serif', fontSize: 13 },
  center:       { display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 40 },
  spinner:      { width: 36, height: 36, border: '4px solid #e0e0e0', borderTop: '4px solid #007AFF', borderRadius: '50%', animation: 'spin 0.8s linear infinite' },
  header:       { backgroundColor: '#1a1a2e', padding: '8px 12px', display: 'flex', alignItems: 'center', gap: 16 },
  headerTitle:  { color: '#fff', fontSize: 16, fontWeight: 700 },
  headerSub:    { color: 'rgba(255,255,255,0.6)', fontSize: 13 },
  error:        { padding: '8px 12px', backgroundColor: '#ffe0e0', color: '#cc0000', fontSize: 13 },
  body:         { flex: 1, padding: 8, display: 'flex', flexDirection: 'column', gap: 8, overflow: 'auto' },
  section:      { backgroundColor: '#fff', border: '1px solid #ddd', borderRadius: 4, padding: '8px 10px' },
  sectionTitle: { fontWeight: 700, fontSize: 13, color: '#1a1a2e', marginBottom: 8, borderBottom: '1px solid #eee', paddingBottom: 4 },
  checkRow:     { display: 'flex', gap: 16, marginBottom: 8 },
  checkLabel:   { display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' },
  fieldRow:     { display: 'flex', gap: 8, marginBottom: 6 },
  field:        { flex: 1, display: 'flex', flexDirection: 'column', gap: 2 },
  label:        { fontSize: 12, color: '#666', fontWeight: 600 },
  input:        { padding: '4px 6px', border: '1px solid #ccc', borderRadius: 3, fontSize: 13, backgroundColor: '#fafafa', outline: 'none' },
  naicBtn:      { padding: '4px 10px', backgroundColor: '#007AFF', color: '#fff', border: 'none', borderRadius: 3, fontSize: 12, cursor: 'pointer' },
  table:        { width: '100%', borderCollapse: 'collapse' },
  thead:        { backgroundColor: '#d0d8e8' },
  th:           { padding: '5px 8px', textAlign: 'left', fontWeight: 700, fontSize: 12, borderBottom: '2px solid #aab', borderRight: '1px solid #ccd' },
  td:           { padding: '4px 8px', borderBottom: '1px solid #eee', fontSize: 13 },
  empty:        { padding: 20, textAlign: 'center', color: '#999' },
  selectedBadge:{ backgroundColor: '#007AFF', color: '#fff', fontSize: 11, borderRadius: 4, padding: '1px 6px', marginLeft: 6 },
  actionBtn:    { padding: '2px 8px', backgroundColor: '#fff', color: '#007AFF', border: '1px solid #007AFF', borderRadius: 3, fontSize: 12, cursor: 'pointer' },
  btnRow:       { display: 'flex', gap: 8, padding: '4px 0' },
  newPolicyBtn: { padding: '5px 14px', backgroundColor: '#1a1a2e', color: '#fff', border: 'none', borderRadius: 4, fontSize: 13, cursor: 'pointer' },
  renewBtn:     { padding: '5px 14px', backgroundColor: '#fff', color: '#333', border: '1px solid #bbb', borderRadius: 4, fontSize: 13, cursor: 'pointer' },
  footer:       { backgroundColor: '#e8e8e8', borderTop: '2px solid #bbb', padding: '6px 12px', display: 'flex', justifyContent: 'flex-end', gap: 8 },
  applyBtn:     { padding: '5px 20px', backgroundColor: '#007AFF', color: '#fff', border: 'none', borderRadius: 4, fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  closeBtn:     { padding: '5px 20px', backgroundColor: '#fff', color: '#333', border: '1px solid #bbb', borderRadius: 4, fontSize: 14, cursor: 'pointer' },
};